import React, { useEffect, useState } from 'react';
import { DialogContent, Stack, Typography, IconButton, ListItem, Button, ListItemButton, List, ListItemText, DialogActions, useTheme, Radio, ListItemIcon, ListItemSecondaryAction, Box } from '@mui/material';
import { useShipments } from '@features/shipments/hooks/useShipments';
import { Shipment, TransportMethod } from '@domain/shipment';
import DialogHeader from '@shared/ui/dialog/DialogHeader';
import { Add, Close, Edit } from '@mui/icons-material';
import { Product } from '@domain/product/models/Product';
import ShipmentPreview from '@features/purchasing/components/preview/ShipmentPreview';
import { CreateShipmentDetails } from '@features/shipments/components/create/CreateShipmentDetails';
import { EditShipmentDetails } from '@features/shipments/components/edit/EditShipmentDetails';
import { PurchaseOrder, PurchaseOrderItem } from '@domain/purchasing';
import { BottomSheet } from '@shared/shell/bottomsheet/BottomSheet';
import { usePurchaseOrderItems } from '@features/purchasing/hooks';
import { Icon, IconSize } from '@shared/icons';
import { SelectableProductDisplay } from '@features/shipments/components/shared';
import { startOfDay } from 'date-fns';
import { TransportMethodIcon } from '@features/shipments/components/shared/TransportMethodIcon';

interface PurchaseOrderShipmentProps {
    selectedShipment: Shipment | undefined;
    handleClose: () => void;
    handleFinish: (shipment: Shipment) => void;
    products: Product[];
    purchaseOrder: PurchaseOrder;
    purchaseOrderItems: PurchaseOrderItem[];

};

export const PurchaseOrderShipment: React.FC<PurchaseOrderShipmentProps> = (props) => {


    const theme = useTheme();

    const { shipments } = useShipments();
    const [bottomSheetIsOpen, setBottomSheetIsOpen] = useState(false);
    const [editBottomSheetIsOpen, setEditBottomSheetIsOpen] = useState(false);

    const [selectedItems, setSelectedItems] = useState<PurchaseOrderItem[]>(() => {
        const withoutShipment = props.purchaseOrderItems.filter(
            (item) => item.shipment === undefined
        );
        if (withoutShipment.length > 0) return withoutShipment;

        if (props.selectedShipment) {
            const matching = props.purchaseOrderItems.filter(
                (item) => item.shipment?.id === props.selectedShipment?.id
            );
            if (matching.length > 0) return matching;
        }

        return props.purchaseOrderItems;
    });
    const [selectedShipment, setSelectedShipment] = useState<Shipment | undefined>(props.selectedShipment);

    const [showAllShipments, setShowAllShipments] = useState(false);
    const [filteredShipments, setFilteredShipments] = useState<Shipment[]>([]);
    const [shipmentName, setShipmentName] = useState<string>('');

    const [editingShipment, setEditingShipment] = useState<Shipment | undefined>(undefined);


    const { updatePurchaseOrderItem } = usePurchaseOrderItems();

    const { createShipment } = useShipments();

    useEffect(() => {
        setFilteredShipments(shipments?.filter(shipment => startOfDay(shipment.departureDate) >= startOfDay(new Date())) || []);
    }, [shipments]);

    useEffect(() => {
        if (props.selectedShipment && props.selectedShipment.name !== shipmentName) {
            setShipmentName(props.selectedShipment.name || '');
        }
    }, [props.selectedShipment, shipmentName]);

    const handleShowAllShipments = () => {
        setShowAllShipments(!showAllShipments);

        if (showAllShipments) {
            setFilteredShipments(shipments?.filter(shipment => startOfDay(shipment.departureDate) >= startOfDay(new Date())) || []);
        } else {
            setFilteredShipments(shipments || []);
        }
    };

    const handleNewShipment = async (shipment?: Shipment) => {
        if (shipment) {
            const newShipment = await createShipment(shipment);
            setSelectedShipment(newShipment);
        }

        setBottomSheetIsOpen(false);
    }

    const handleEditShipment = (shipment?: Shipment) => {
        if (shipment) {
            setSelectedShipment(shipment);
        }
        setEditingShipment(shipment);
        setEditBottomSheetIsOpen(true);
    }

    const handleSelectShipment = (shipment: Shipment) => {
        for (const item of selectedItems) {
            if (item.id) {
                item.shipment = shipment;
                updatePurchaseOrderItem({ itemId: item.id, shipmentId: shipment.id, orderId: props.purchaseOrder.id });
            }
        }

        // Update purchase order items with new shipment
        setBottomSheetIsOpen(false);
        props.handleFinish(shipment);
    }

    const handleSelectionChange = (selectedItems: PurchaseOrderItem[]) => {
        setSelectedItems(selectedItems);
    }

    return (
        <>
            <DialogHeader>
                <Stack direction="row" alignItems="center" justifyContent="space-between">

                    <Typography variant="h6" component="div">
                        How do you want to ship the product?
                    </Typography>

                    <IconButton onClick={props.handleClose}>
                        <Close />
                    </IconButton>

                </Stack>
            </DialogHeader>

            <DialogContent sx={{ p: 0 }}>

                <Stack spacing={2} justifyContent="flex-start" alignItems="flex-start" sx={{ p: 4, borderBottom: 1, borderColor: theme.palette.divider }}>
                    <Stack spacing={2}>
                        <SelectableProductDisplay
                            purchaseOrderItems={props.purchaseOrderItems.map(item => ({ item, selected: selectedItems.some(selectedItem => selectedItem.id === item.id) }))}
                            onSelectionChange={handleSelectionChange} />
                    </Stack>
                </Stack>


                <Stack spacing={2} position="sticky" zIndex={99} top={0} flex={1} direction="row" justifyContent="space-between" alignItems="center" sx={{ py: 2, px: 4, backgroundColor: theme.palette.background.default, borderBottom: 1, borderColor: theme.palette.divider }}>

                    <Typography variant="subtitle2" color="text.primary">Select a shipment</Typography>

                    <Button variant="text" color="secondary" startIcon={<Add />} onClick={() => setBottomSheetIsOpen(true)}>Create a new shipment</Button>

                </Stack>

                <List sx={{ p: 0 }}>

                    {shipments?.length === 0 ? (
                        <ListItem divider sx={{ p: 4 }}>
                            <ListItemIcon> <TransportMethodIcon size={40} transportMethod={TransportMethod.BOAT} /> </ListItemIcon>
                            <ListItemText primary="No shipments found" secondary="Create your first shipment to get started" />
                        </ListItem>
                    ) : (
                        filteredShipments?.length === 0 && (
                            <ListItem divider sx={{ p: 4 }}>
                                <ListItemText primary="No upcoming shipments found" />
                            </ListItem>
                        )
                    )}


                    {filteredShipments?.sort((a, b) => b.departureDate.getTime() - a.departureDate.getTime()).map(shipment => (
                        <ListItem
                            divider
                            key={shipment.id}
                            sx={{
                                p: 4,
                                gap: 2
                            }}
                        >

                            <Stack direction="row" alignItems="center" gap={2} flex={1}>
                                <Radio
                                    checked={selectedShipment?.id === shipment.id}
                                    onChange={() => setSelectedShipment(shipment)}
                                />
                                <ShipmentPreview shipment={shipment} />
                                <IconButton
                                    className='edit-icon'
                                    onClick={() => {
                                        handleEditShipment(shipment);
                                    }}>
                                    <Icon icon="IcEdit" />
                                </IconButton>
                            </Stack>
                        </ListItem>
                    ))}

                    {shipments !== undefined && shipments?.length >= (filteredShipments?.length || 0) && (
                        <ListItem alignItems="center" sx={{ p: 4 }}>
                            <Button variant="text" color="secondary" fullWidth startIcon={<Add />} onClick={() => handleShowAllShipments()}>
                                {showAllShipments ? 'Hide departed shipments' : 'Show departed shipments'}
                            </Button>
                        </ListItem>
                    )}

                </List  >
            </DialogContent >

            <DialogActions sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
                <Button variant="contained"
                    disabled={!selectedShipment || selectedItems.length === 0}
                    onClick={() => handleSelectShipment(selectedShipment!)}>Add shipment to {selectedItems.length} item{selectedItems.length > 1 ? 's' : ''}
                </Button>
            </DialogActions>


            <BottomSheet
                open={bottomSheetIsOpen}
                onClose={() => setBottomSheetIsOpen(false)}
            >
                <CreateShipmentDetails
                    onClose={() => setBottomSheetIsOpen(false)}
                    onFinish={handleNewShipment}
                    purchaseOrderItems={props.purchaseOrderItems}
                />
            </BottomSheet>

            <BottomSheet
                open={editBottomSheetIsOpen}
                onClose={() => setEditBottomSheetIsOpen(false)}
            >
                <EditShipmentDetails
                    shipmentId={editingShipment?.id || ''}
                    onClose={() => setEditBottomSheetIsOpen(false)}
                    onFinish={() => setEditBottomSheetIsOpen(false)}
                />
            </BottomSheet>
        </>
    );
};
